import cx from 'classnames';
import {useFormikContext} from 'formik';
import {get, isEmpty, isNumber, map} from 'lodash';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {InputFormik, InputProps, NumericFieldFormik, PhoneInputFormik, SelectFormik} from 'components';
import {EMAIL_MAX_LENGTH} from 'constants/client';
import {useUserInfoSchema} from 'hooks';
import {AccountAssignmentInfo, CustomField, CustomFieldType} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InviteeParticipantRole} from 'types';
import {sessionUtils, userInfoUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMMON.SESSION.LABELS;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON.ALL;

type Props = {
    hideTitle?: boolean;
    basePath?: string;
    assignment?: AccountAssignmentInfo;
    role?: InviteeParticipantRole;
    guestEnabled?: boolean;
};

// rename to InviteeInfoControls
export const ClientInfoControls: React.FC<Props> = ({
    basePath = 'userInfo', assignment, guestEnabled, role = InviteeParticipantRole.Client, hideTitle,
}: Props) => {
    const {t} = useTranslation();
    const {errors} = useFormikContext();
    const {getUserInfoFields, getControlOnlyFields} = useUserInfoSchema({role, fetchOnInit: true, guestEnabled});

    const [userInfoFieldsRaw, controlOnlyFields] = useMemo(() => [
        getUserInfoFields(assignment),
        getControlOnlyFields(assignment)
    ], [getUserInfoFields, getControlOnlyFields, assignment]);

    const userInfoFields = useMemo(() => map(userInfoFieldsRaw, (field) => ({
        ...field,
        title: userInfoUtils.getUserInfoFieldLabel(field, t)
    })), [userInfoFieldsRaw, t]);

    const fieldPath = useMemo(() => sessionUtils.getUserInfoFieldsPath({pathPrefix: basePath}), [basePath]);

    const getClassName = useCallback((count: number) => {
        if (count === 2) {
            return 'md:tw-grid-cols-2';
        } else if (count > 2) {
            return 'md:tw-grid-cols-3';
        } else {
            return '';
        }
    }, []);

    const renderField = useCallback((name: string, field: CustomField, params?: Partial<InputProps>) => {
        const fieldProps = {
            hasError: !!get(errors, name),
            key: name,
            name,
            label: field.title,
            required: field.required
        };
        if (field.name.includes('phone')) {
            return (
                <PhoneInputFormik
                    {...fieldProps}
                    {...params}
                />
            );
        } else if (field.name.includes('email')) {
            return (
                <InputFormik
                    {...fieldProps}
                    type='email'
                    {...params}
                    maxLength={EMAIL_MAX_LENGTH}
                />
            );
        } else if (field.enum) {
            const options = map(field.enum, (enumValue) => {
                const fieldText = enumValue;
                return {
                    id: enumValue, name: fieldText
                };
            });

            if (field.nullable) {
                options.unshift({
                    id: null,
                    name: t(T_KEYS_COMMON.NONE)
                });
            }

            return (
                <SelectFormik
                    {...fieldProps}
                    placeholder={field.title}
                    options={options}
                    valueAs='id'
                    {...params}
                />
            );
        } else if (field.type === CustomFieldType.Number) {
            return (
                <NumericFieldFormik
                    {...fieldProps}
                    {...isNumber(field.minimum) && {min: field.minimum}}
                    {...isNumber(field.maximum) && {max: field.maximum}}
                    className={params?.className}
                    spacing={params?.spacing}
                />);
        } else {
            return (
                <InputFormik
                    {...fieldProps}
                    {...field.pattern && {pattern: field.pattern}}
                    {...isNumber(field.minLength) && {minLength: field.minLength}}
                    {...isNumber(field.maxLength) && {maxLength: field.maxLength}}
                    {...params}
                />
            );
        }
    }, [t, errors]);

    const renderControlOnlyField = useCallback((field: CustomField) => {
        const name = `${fieldPath.CONTROL_ONLY_INFO}.${field.name}`;
        return renderField(name, field, {spacing: 'top', className: 'tw-mt-controls'});
    }, [fieldPath, renderField]);

    const renderUserInfoField = useCallback((field: CustomField) => {
        const name = userInfoUtils.getUserInfoFullPath(field.name, basePath);
        return renderField(name, field, {className: 'tw-mt-controls'});
    }, [basePath, renderField]);

    const fieldsClassName = useMemo(() => getClassName(userInfoFields.length), [userInfoFields, getClassName]);
    const controlOnlyFieldsClassName = useMemo(() => getClassName(controlOnlyFields.length), [controlOnlyFields, getClassName]);

    return (
        <Fragment>
            <div className='tw-mb-9'>
                {!hideTitle && <div className='tw-mb-2'>{t(T_KEYS.CLIENT_INFO)}</div>}
                {!isEmpty(userInfoFields) &&
                    <div className={cx('tw-grid md:tw-gap-4', fieldsClassName)}>
                        {map(userInfoFields, renderUserInfoField)}
                    </div>}
            </div>
            {!isEmpty(controlOnlyFields) && (
                <div className='tw-mb-9 tw-text-nowrap'>
                    <div>{t(T_KEYS.CONTROL_ONLY_INFO)}</div>
                    <div className={cx('tw-grid md:tw-gap-4', controlOnlyFieldsClassName)}>
                        {map(controlOnlyFields, renderControlOnlyField)}
                    </div>
                </div>
            )}
        </Fragment>
    );
};